import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { userSelectors } from 'reducers';
import { roleCategories } from 'utils/constants';
import { useGetTopBarLogoUrl } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import InvestorNavigation from './InvestorNavigation';
import InvestorNavigationSmall from './InvestorNavigationSmall';
import { ActionButton, InfoDialog, Loader, QuitButton } from 'components';
import { AppBar, Box, IconButton, Typography } from '@mui/material';
import * as icons from 'mdi-material-ui';
import {
    useSubscriptionFormSignatureStatus,
    useUserRoleMatchCategory,
} from 'utils/hooks/subscriptions';
import { useTunnelFlow } from 'utils/hooks/tunnel';
import { useSmallScreen } from 'utils/hooks/ui';

const barPaddingSize = '50px';
const buttonsWidth = 180;

const TopBar = ({ inTunnel = false, mode = 'auto' }) => {
    const location = useLocation();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const hasFinishedSubscription = useSelector(userSelectors.getFinishedSubscriptionStatus);

    const [assistanceOpen, setAssistanceOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const anchorRef = useRef(null);

    const userIsInvestor = useUserRoleMatchCategory(roleCategories.INVESTORS);
    const userIsAdminDistributor = useUserRoleMatchCategory(roleCategories.ADMIN_DISTRIBUTORS);
    const userIsDistributor = useUserRoleMatchCategory(roleCategories.DISTRIBUTORS);

    const isSmallScreen = useSmallScreen('md');

    const { logoUrl, loading: logoLoading, distributorLogoUrl } = useGetTopBarLogoUrl();

    const distributorLogoURL = useMemo(
        () =>
            tunnelSubscription?.distributorCompany?.companyLogo?.url ||
            currentSubscription?.distributorCompany?.companyLogo?.url ||
            distributorLogoUrl,
        [currentSubscription, tunnelSubscription, distributorLogoUrl]
    );

    // Location-based conditions
    const isNonInitialSubscription = useMemo(
        () => location.pathname.includes('new-subscription'),
        [location]
    );
    const isSubscribing = useMemo(() => location.pathname.includes('sign-up'), [location]);
    const isAbandoning = useMemo(() => location.pathname.includes('abandonner'), [location]);

    const hasSubscriberSignedBS = useSubscriptionFormSignatureStatus(tunnelSubscription);

    const isTunnelPath = useTunnelFlow() || inTunnel;

    // Auto-detect mode based on location and context
    const detectedMode = useMemo(() => {
        if (mode !== 'auto') return mode;

        const { pathname } = location;

        // Define path patterns for better readability
        const isAuthPath = ['sign-in', 'forgot-password', 'reset-password', 'setup-password'].some(
            path => pathname.includes(path)
        );

        const isMainAppPath = !['auth', 'password', 'sign-up', 'onboarding', 'abandonner'].some(
            path => pathname.includes(path)
        );

        // 1. TUNNEL MODE - Subscription/onboarding flows
        if (isTunnelPath) return 'tunnel';

        // 2. AUTH MODE - Authentication and password pages
        if (isAuthPath) return 'auth';

        // 3. DASHBOARD MODE - Main app with navigation
        if (
            internalUser?.impersonatedByUser ||
            (userIsInvestor &&
                (hasSubscriberSignedBS ||
                    (!isNonInitialSubscription && hasFinishedSubscription))) ||
            (!userIsInvestor && !userIsDistributor && isMainAppPath)
        ) {
            return 'dashboard';
        }

        // 4. BASIC MODE - Fallback for simple cases
        return 'basic';
    }, [
        mode,
        location,
        isTunnelPath,
        internalUser?.impersonatedByUser,
        userIsInvestor,
        hasSubscriberSignedBS,
        isNonInitialSubscription,
        hasFinishedSubscription,
        userIsDistributor,
    ]);

    const handleToggle = () => {
        setMenuOpen(prevOpen => !prevOpen);
    };

    // Determine styling based on mode
    const getAppBarStyles = useCallback(
        theme => {
            const baseStyles = {
                alignItems: 'center',
                flexDirection: 'row',
                height: `${theme.constants.appBarHeight}px`,
                position: 'relative',
            };

            switch (detectedMode) {
                case 'tunnel':
                    return {
                        ...baseStyles,
                        background: internalUser?.impersonatedByUser
                            ? theme.palette.substitutionMode.main
                            : theme.palette.common.white,
                        padding: `8px ${barPaddingSize}`,
                        justifyContent: isAbandoning ? 'flex-end' : 'space-between',
                    };
                case 'dashboard':
                    return {
                        ...baseStyles,
                        justifyContent: 'space-between',
                        background: internalUser?.impersonatedByUser
                            ? theme.palette.substitutionMode.main
                            : theme.palette.grey[100],
                        padding: '8px 50px',
                        [theme.breakpoints.down('md')]: {
                            padding: '0 30px 0 0',
                        },
                    };
                case 'auth':
                case 'basic':
                default:
                    return {
                        ...baseStyles,
                        background: internalUser?.impersonatedByUser
                            ? theme.palette.substitutionMode.main
                            : theme.palette.common.white,
                        justifyContent: internalUser?.impersonatedByUser
                            ? 'space-between'
                            : 'flex-end',
                        padding: '8px 50px',
                    };
            }
        },
        [detectedMode, internalUser?.impersonatedByUser, isAbandoning]
    );

    // Render assistance button (tunnel mode)
    const renderAssistanceButton = () => {
        if (detectedMode !== 'tunnel') return null;
        if (
            !(
                (isSubscribing || isNonInitialSubscription) &&
                !internalUser?.impersonatedByUser &&
                !isAbandoning
            )
        )
            return null;

        return (
            <ActionButton
                size="small"
                variant="text"
                sx={theme => ({
                    color: theme.palette.grey[600],
                    minWidth: '10px',
                    zIndex: 10,
                    '&:hover': {
                        background: 'transparent',
                        color: theme.palette.primary.main,
                    },
                })}
                onClick={() => setAssistanceOpen(true)}
            >
                {isSmallScreen ? <icons.InformationOutline /> : texts.ASSISTANCE}
            </ActionButton>
        );
    };

    // Render logo
    const renderLogo = () => {
        if (detectedMode === 'basic' || detectedMode === 'auth') return null;

        const imageStyles = {
            maxHeight: '100%',
            maxWidth: '248px',
            ml: '20px',
            alignSelf: 'center',
        };

        const logoBoxStyles = {
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: internalUser?.impersonatedByUser ? '0 1' : '1 1',
        };

        if (detectedMode === 'tunnel') {
            return (
                <Box sx={logoBoxStyles} component="span">
                    <Loader loading={logoLoading} sx={{ mt: 0 }}>
                        {!logoUrl && !distributorLogoURL && ' '}
                        <Box src={logoUrl} component="img" sx={imageStyles} />
                        {!!distributorLogoURL && !(userIsAdminDistributor || userIsDistributor) && (
                            <Box
                                textAlign="center"
                                src={distributorLogoURL}
                                component="img"
                                sx={{ maxHeight: '100%', alignSelf: 'center' }}
                            />
                        )}
                    </Loader>
                </Box>
            );
        }

        if (detectedMode === 'dashboard') {
            return (
                <Loader loading={logoLoading} sx={{ flex: '0 1 248px', mt: 0 }}>
                    <Box
                        flex="0 1"
                        textAlign="center"
                        src={logoUrl}
                        component="img"
                        sx={imageStyles}
                    />
                </Loader>
            );
        }

        return null;
    };

    // Render navigation (dashboard mode)
    const renderNavigation = () => {
        if (detectedMode !== 'dashboard' || isSmallScreen) return null;

        if (
            (userIsInvestor &&
                (hasSubscriberSignedBS ||
                    (!isNonInitialSubscription && hasFinishedSubscription))) ||
            internalUser?.impersonatedByUser
        ) {
            return (
                <Box
                    flex="1 1"
                    display="flex"
                    justifyContent="center"
                    alignContent="center"
                    flexDirection="column"
                    marginRight={`-${buttonsWidth}px`}
                >
                    {userIsInvestor && (
                        <InvestorNavigation
                            hasSubscriberSignedBS={hasSubscriberSignedBS}
                            isNewSubscription={isNonInitialSubscription}
                            hasFinishedSubscription={hasFinishedSubscription}
                            userIsAdminDistributor={userIsAdminDistributor}
                        />
                    )}
                    {!!internalUser?.impersonatedByUser && (
                        <Typography
                            variant="h5"
                            align="center"
                            sx={{ marginTop: '15px', flex: '1 1' }}
                        >
                            {userIsInvestor && texts.IMPERSONATE_SUBSCRIBER}
                            {(userIsAdminDistributor || userIsDistributor) &&
                                texts.IMPERSONATE_DISTRIBUTOR}
                            {!userIsAdminDistributor &&
                                !userIsInvestor &&
                                !userIsDistributor &&
                                texts.SUBSTITUTION_MODE}
                        </Typography>
                    )}
                </Box>
            );
        }

        return null;
    };

    // Render mobile menu button (dashboard mode)
    const renderMobileMenuButton = () => {
        if (detectedMode !== 'dashboard' || !isSmallScreen) return null;

        return (
            <IconButton
                size="small"
                ref={anchorRef}
                onClick={handleToggle}
                color={menuOpen ? 'primary' : 'grey'}
                sx={theme => ({
                    '&:hover': { background: 'transparent', color: theme.palette.primary.main },
                })}
                disableRipple
            >
                <icons.Menu sx={{ color: 'inherit', fontSize: '40px' }} />
            </IconButton>
        );
    };

    // Render impersonation text (tunnel/basic modes)
    const renderImpersonationText = () => {
        if (!internalUser?.impersonatedByUser) return null;

        if (detectedMode === 'tunnel') {
            return (
                <Typography
                    variant="h5"
                    align="center"
                    sx={{ mt: '15px', ml: `-${barPaddingSize}` }}
                >
                    {userIsInvestor && texts.IMPERSONATE_SUBSCRIBER}
                    {(userIsAdminDistributor || userIsDistributor) && texts.IMPERSONATE_DISTRIBUTOR}
                    {!userIsAdminDistributor &&
                        !userIsInvestor &&
                        !userIsDistributor &&
                        texts.SUBSTITUTION_MODE}
                </Typography>
            );
        }

        if (detectedMode === 'basic' || detectedMode === 'auth') {
            return (
                <Typography
                    variant="h5"
                    align="center"
                    sx={{ marginTop: '15px', marginLeft: '-130px' }}
                >
                    {texts.SUBSTITUTION_MODE}
                </Typography>
            );
        }

        return null;
    };

    // Render quit button
    const renderQuitButton = () => {
        const getQuitButtonStyles = () => {
            switch (detectedMode) {
                case 'tunnel':
                    if (internalUser?.impersonatedByUser) {
                        return {
                            flex: '0 1 180px',
                            maxWidth: '180px',
                            display: 'flex',
                            justifyContent: 'flex-end',
                        };
                    }
                    // if there's no logo, float the button to the right
                    return !logoUrl && !distributorLogoURL
                        ? { float: 'right' }
                        : { maxWidth: '180px' };
                case 'dashboard':
                    return isSmallScreen
                        ? {}
                        : {
                              flex: `0 1 ${buttonsWidth}px`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                          };
                default:
                    return {};
            }
        };

        if (userIsInvestor && isSmallScreen && detectedMode !== 'tunnel') return null;

        return (
            <QuitButton
                inTunnel={detectedMode === 'tunnel' || inTunnel}
                sx={getQuitButtonStyles()}
            />
        );
    };

    return (
        <>
            <AppBar sx={getAppBarStyles} elevation={0}>
                {renderAssistanceButton()}
                {renderLogo()}
                {renderNavigation()}
                {renderMobileMenuButton()}
                {renderImpersonationText()}
                {renderQuitButton()}
            </AppBar>

            {/* Mobile navigation for dashboard mode */}
            {detectedMode === 'dashboard' && (
                <InvestorNavigationSmall
                    menuOpen={menuOpen}
                    setMenuOpen={setMenuOpen}
                    anchorRef={anchorRef}
                    hasSubscriberSignedBS={hasSubscriberSignedBS}
                    isNewSubscription={isNonInitialSubscription}
                    userIsInvestor={userIsInvestor}
                />
            )}

            {/* Assistance dialog for tunnel mode */}
            {detectedMode === 'tunnel' && (
                <InfoDialog
                    open={assistanceOpen}
                    title={texts.ASSISTANCE}
                    message={texts.ASSISTANCE_DIRECTIONS}
                    handleClose={() => setAssistanceOpen(false)}
                />
            )}
        </>
    );
};

TopBar.propTypes = {
    inTunnel: PropTypes.bool,
    mode: PropTypes.oneOf(['auto', 'tunnel', 'dashboard', 'auth', 'basic']),
};

export default TopBar;
